import { buildTitle, buildLegend, buildGrid, buildAxisStyle, buildToolbox, buildDataZoom, getAnalyticsColors, buildTooltip } from './AnalyticsConfig';
import { theme } from '../Utils/theme';

// small local helper to validate numeric values
const safeNumber = (value) => (typeof value === 'number' && Number.isFinite(value) ? value : 0);

const buildBarOption = ({ title, subtitle, xAxisData, series }) => ({
  title: buildTitle(title, subtitle),
  legend: buildLegend('bottom'),
  grid: buildGrid(),
  xAxis: {
    type: 'category',
    data: xAxisData,
    axisTick: { alignWithLabel: true },
    ...buildAxisStyle(),
  },
  yAxis: {
    type: 'value',
    ...buildAxisStyle(),
  },
  series,
  color: getAnalyticsColors(),
  toolbox: buildToolbox(),
  dataZoom: buildDataZoom(),
});

const formatRevenue = (value) => {
  if (typeof value !== 'number' || Number.isNaN(value) || !Number.isFinite(value)) {
    return '$0';
  }

  if (Math.abs(value) >= 1_000_000) {
    return `$${(value / 1_000_000).toFixed(2)}M`;
  }

  if (Math.abs(value) >= 1_000) {
    return `$${Math.round(value / 1_000)}K`;
  }

  return `$${value.toLocaleString()}`;
};

const truncateLabel = (value, maxLength = 18) => {
  if (typeof value !== 'string') return value;
  return value.length > maxLength ? `${value.slice(0, maxLength - 3)}...` : value;
};

export const providerPerformanceOption = ({ data }) => {
  const providerNames = data.map((item) => item.name);
  const revenueValues = data.map((item) => item.revenue);
  const cumulativeValues = data.map((item) => item.cumulativePercent);

  return {
    title: buildTitle('Provider Performance', 'Revenue Contribution by Provider'),
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      formatter: (params) => {
        const revenuePoint = params.find((point) => point.seriesName === 'Revenue');
        const cumulativePoint = params.find((point) => point.seriesName === 'Cumulative %');
        const index = revenuePoint?.dataIndex ?? 0;
        const provider = data[index] || {};

        return [
          `<div style="padding: 8px; color: ${theme.chartTooltipText()}; font-size: 13px; line-height: 1.5;">`,
          `<strong>${provider.name || revenuePoint?.name || ''}</strong>`,
          `<br/>Revenue: <strong>${formatRevenue(revenuePoint?.value)}</strong>`,
          `<br/>Visits: <strong>${provider.count ?? 'N/A'}</strong>`,
          `<br/>Contribution: <strong>${provider.contributionPercent?.toFixed(1) ?? '0'}%</strong>`,
          `<br/>Cumulative: <strong>${cumulativePoint?.value?.toFixed(1) ?? '0'}%</strong>`,
          `</div>`,
        ].join('');
      },
    },
    legend: {
      data: ['Revenue', 'Cumulative %'],
      top: 2,
      right: 16,
      textStyle: { color: theme.chartAxisLabel() },
      itemGap: 20,
      itemWidth: 14,
      itemHeight: 14,
      orient: 'horizontal',
    },
    grid: {
      left: '5%',
      right: '7%',
      top: '25%',
      bottom: '5%',
      containLabel: true,
    },
    xAxis: {
      type: 'category',
      data: providerNames,
      axisTick: { alignWithLabel: true },
      axisLabel: {
        color: theme.chartAxisLabel(),
        fontSize: 11,
        interval: 0,
        rotate: 35,
        formatter: (value) => truncateLabel(value, 16),
      },
      axisLine: { lineStyle: { color: theme.chartAxisLine() } },
    },
    yAxis: [
      {
        type: 'value',
        name: 'Revenue ($)',
        nameTextStyle: { color: theme.chartAxisLabel(), fontSize: 12 },
        axisLine: { lineStyle: { color: theme.chartAxisLine() } },
        splitLine: { lineStyle: { color: theme.chartGridLine() } },
        axisLabel: {
          color: theme.chartAxisLabel(),
          fontSize: 11,
          formatter: (value) => formatRevenue(value),
        },
      },
      {
        type: 'value',
        name: 'Cumulative Revenue %',
        nameTextStyle: { color: theme.chartAxisLabel(), fontSize: 12 },
        axisLine: { lineStyle: { color: theme.chartAxisLine() } },
        splitLine: { show: false },
        axisLabel: {
          color: theme.chartAxisLabel(),
          fontSize: 11,
          formatter: '{value}%',
        },
        min: 0,
        max: 100,
        interval: 20,
      },
    ],
    series: [
      {
        name: 'Revenue',
        type: 'bar',
        data: revenueValues,
        barWidth: 36,
        itemStyle: {
          color: theme.chartHighlight(),
          borderRadius: [8, 8, 0, 0],
        },
        emphasis: {
          itemStyle: { color: theme.chartHighlightEmphasis() },
        },
        label: {
          show: true,
          position: 'top',
          color: theme.chartTitleText(),
          fontSize: 11,
          formatter: ({ value }) => formatRevenue(value),
        },
        animationDuration: 900,
        progressive: data.length > 50 ? 200 : 0,
        progressiveThreshold: 100,
      },
      {
        name: 'Cumulative %',
        type: 'line',
        yAxisIndex: 1,
        data: cumulativeValues,
        smooth: true,
        symbol: 'circle',
        symbolSize: 8,
        itemStyle: {
          color: theme.chartSuccess(),
          borderColor: theme.chartSurface(),
          borderWidth: 2,
        },
        lineStyle: {
          width: 3,
          color: theme.chartSuccess(),
        },
        emphasis: {
          focus: 'series',
        },
        markLine: {
          silent: true,
          symbol: 'none',
          lineStyle: {
            type: 'dashed',
            color: theme.chartSuccess(),
            width: 2,
          },
          label: {
            show: true,
            formatter: '80% Revenue Threshold',
            position: 'insideEndTop',
            color: theme.chartAxisLabel(),
            fontSize: 11,
          },
          data: [{ yAxis: 80 }],
        },
        animationDuration: 900,
      },
    ],
    toolbox: buildToolbox(),
    dataZoom: buildDataZoom(),
  };
};

export const facilityPatientsOption = ({ data }) => buildBarOption({
  title: 'Facility-wise Patient Count',
  subtitle: 'Patient volume per facility',
  xAxisData: data.map((item) => item.name),
  series: [
    {
      name: '',
      type: 'bar',
      data: data.map((item) => item.value),
      barMaxWidth: '30%',
      itemStyle: { borderRadius: [6, 6, 0, 0] },
    },
  ],
});

export const appointmentStatusOption = ({ data }) => ({
  title: buildTitle('Appointment Status', 'Status breakdown across facilities'),
  legend: buildLegend('bottom'),
  tooltip: { trigger: 'item', formatter: '{b}: {c} ({d}%)' },
  series: [
    {
      type: 'pie',
      radius: ['42%', '68%'],
      center: ['50%', '55%'],
      label: {
        show: false,
      },
      emphasis: { label: { show: true, fontSize: 14, fontWeight: 'bold' } },
      data,
    },
  ],
  color: getAnalyticsColors(),
  toolbox: buildToolbox(),
});

export const facilityPerformanceRadarOption = ({ data }) => {
  const indicator = [
    { name: 'Patients', max: Math.max(...data.map((item) => item.values[0]), 1) },
    { name: 'Appointments', max: Math.max(...data.map((item) => item.values[1]), 1) },
    { name: 'Visits', max: Math.max(...data.map((item) => item.values[2]), 1) },
    { name: 'Seen', max: Math.max(...data.map((item) => item.values[3]), 1) },
    { name: 'Cancelled', max: Math.max(...data.map((item) => item.values[4]), 1) },
  ];

  return {
    title: buildTitle('Facility Performance Radar', 'Compare core facility metrics'),
    tooltip: { trigger: 'item' },
    legend: buildLegend('bottom'),
    radar: {
      indicator,
      shape: 'circle',
      splitLine: { lineStyle: { color: theme.chartAxisLine() } },
      axisLine: { lineStyle: { color: theme.chartAxisLine() } },
      splitArea: { areaStyle: { color: 'transparent' } },
      name: { color: theme.chartAxisLabel() },
    },
    series: [
      {
        name: 'Facility performance',
        type: 'radar',
        data: data.map((item) => ({ name: item.name, value: item.values })),
        areaStyle: { opacity: 0.2 },
      },
    ],
    toolbox: buildToolbox(),
  };
};

export const ageDistributionOption = ({ data, title = 'Appointment Age Distribution', subtitle = 'Age groups for scheduled appointments' }) => buildBarOption({
  title,
  subtitle,
  xAxisData: data.map((item) => item.name),
  series: [
    {
      name: '',
      type: 'bar',
      data: data.map((item) => item.value),
      itemStyle: {
        color: {
          type: 'linear', x: 0, y: 0, x2: 0, y2: 1,
          colorStops: [{ offset: 0, color: '#52c4c8' }, { offset: 1, color: '#26959a' }]
        },
        borderRadius: [6, 6, 0, 0]
      },
    },
  ],
});

export const stateDistributionOption = ({ data }) => ({
  title: buildTitle('State Distribution', 'Geographic distribution of appointments'),
  tooltip: {
    trigger: 'item',
    formatter: (params) => {
      const state = params.name;
      const patients = params.data?.patients ?? 'N/A';
      const visits = params.data?.visits ?? 'N/A';
      const revenue = params.data?.revenue ?? 0;
      return [
        `<div style="padding: 10px; color: ${theme.chartTooltipText()}; font-size: 13px; line-height: 1.5;">`,
        `<strong>${state}</strong>`,
        `<br/>Patients: <strong>${patients.toLocaleString ? patients.toLocaleString() : patients}</strong>`,
        `<br/>Visits: <strong>${visits.toLocaleString ? visits.toLocaleString() : visits}</strong>`,
        `</div>`,
      ].join('');
    },
    backgroundColor: theme.chartTooltipBg(),
    borderColor: theme.chartAxisLine(),
    borderWidth: 1,
    extraCssText: `box-shadow: 0 10px 28px rgba(${theme.chartTooltipTextRgb()}, 0.18); border-radius: 8px;`,
  },
  geo: {
    map: 'USA',
    roam: true,
    zoom: 1.2,
    label: { show: false },
    itemStyle: {
      borderColor: theme.chartSurface(),
      borderWidth: 1,
      areaColor: theme.chartAreaLight(),
    },
    emphasis: {
      itemStyle: {
        areaColor: theme.chartBlue500(),
        borderColor: theme.chartBlue800(),
        borderWidth: 1.5,
      },
    },
  },
  visualMap: {
    min: 0,
    max: Math.max(...data.map((item) => item.value), 1),
    calculable: true,
    orient: 'horizontal',
    left: 'center',
    bottom: 10,
    inRange: {
      color: [theme.chartBlue100(), theme.chartBlue600()],
    },
    textStyle: { color: theme.chartAxisLabel() },
  },
  series: [
    {
      name: 'State distribution',
      type: 'map',
      map: 'USA',
      geoIndex: 0,        // <-- key change: render onto the existing geo component
      data: data.map((item) => ({
        name: item.name,
        value: item.value,
        patients: item.patients,
        appointments: item.appointments,
        visits: item.visits,
        revenue: item.revenue,
      })),
    },
  ],
  toolbox: buildToolbox(),
  animationDuration: 900,
  animationEasing: 'cubicOut',
});

export const cityDistributionOption = ({ data }) => ({
  title: buildTitle('City Distribution', 'Top 10 cities for appointments'),
  tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
  grid: buildGrid(),
  xAxis: {
    type: 'value',
    ...buildAxisStyle(),
  },
  yAxis: {
    type: 'category',
    data: data.map((item) => item.name),
    ...buildAxisStyle(),
  },
  series: [
    {
      name: 'Appointments',
      type: 'bar',
      data: data.map((item) => item.value),
      barWidth: 18,
      itemStyle: { borderRadius: 8 },
    },
  ],
  color: [getAnalyticsColors()[2]],
  toolbox: buildToolbox(),
});

export const patientsComparisonOption = ({ data, comparison, name }) => ({
  title: buildTitle(name, `Compare patients against ${comparison}`),
  tooltip: { trigger: 'axis' },
  legend: { show: false },
  grid: buildGrid(),
  xAxis: {
    type: 'category',
    data: data.map((item) => item.name),
    ...buildAxisStyle(),
  },
  yAxis: {
    type: 'value',
    ...buildAxisStyle(),
  },
  series: [
    {
      name: 'Patients',
      type: 'bar',
      data: data.map((item) => item.patients),
      itemStyle: { borderRadius: [6, 6, 0, 0] },
    },
    {
      name: comparison,
      type: 'bar',
      data: data.map((item) => item[comparison.toLowerCase()]),
      itemStyle: { borderRadius: [6, 6, 0, 0] },
    },
  ],
  color: [getAnalyticsColors()[0], getAnalyticsColors()[3]],
  toolbox: buildToolbox(),
  dataZoom: buildDataZoom(),
});

export const facilityTreemapOption = ({ data }) => ({
  title: buildTitle('Facility Treemap', 'Appointments by facility'),
  tooltip: { formatter: ({ data }) => `${data.name}<br/>Appointments: ${data.value}` },
  series: [
    {
      type: 'treemap',
      roam: false,
      nodeClick: false,
      label: { show: true, formatter: '{b}\n{c}', color: theme.chartTooltipText() },
      data,
    },
  ],
  toolbox: buildToolbox(),
});

export const ageHeatmapOption = ({ facilityNames, ageKeys, rows }) => ({
  title: buildTitle('Facility vs Age Heatmap', 'Appointment age intensity by facility'),
  tooltip: { position: 'top' },
  grid: buildGrid(),
  xAxis: {
    type: 'category',
    data: ageKeys,
    ...buildAxisStyle(),
  },
  yAxis: {
    type: 'category',
    data: facilityNames,
    ...buildAxisStyle(),
  },
  visualMap: {
    min: 0,
    max: Math.max(...rows.flat(), 1),
    calculable: true,
    orient: 'horizontal',
    left: 'center',
    bottom: 0,
    textStyle: { color: theme.chartAxisLabel() },
  },
  series: [
    {
      name: 'Age count',
      type: 'heatmap',
      data: rows.flatMap((row, rowIndex) => row.map((value, colIndex) => [colIndex, rowIndex, value])),
      label: { show: false },
      emphasis: { itemStyle: { borderColor: theme.chartSurface(), borderWidth: 1 } },
    },
  ],
  toolbox: buildToolbox(),
});

export const financialScatterOption = ({ data }) => ({
  title: buildTitle('Financial Bubble Chart', 'Insurance pay vs patient payment'),
  tooltip: {
    ...buildTooltip(),
    trigger: 'item',
    extraCssText: `box-shadow: 0 8px 20px rgba(${theme.chartTooltipTextRgb()}, 0.12); border-radius: 8px;`,
    formatter: (params) => {
      const d = params && params.data ? params.data : params;
      const insurance = d[6] ?? d[0] ?? 0;
      const patient = d[7] ?? d[1] ?? 0;
      const visits = d[2] || 0;
      const provider = d[3] || params.seriesName || '';
      const facility = d[4] || params.seriesName || '';
      const cpt = d[5] || '';
      return [
        `<div style="padding:10px;color:${theme.chartTooltipText()};font-size:13px;line-height:1.4;">`,
        `<strong>${facility}</strong>`,
        `<br/>Provider: <strong>${provider}</strong>`,
        `<br/>Insurance Pay: <strong>$${insurance.toLocaleString()}</strong>`,
        `<br/>Patient Payment: <strong>$${patient.toLocaleString()}</strong>`,
        `<br/>Visits: <strong>${visits.toLocaleString()}</strong>`,
        cpt ? `<br/>CPT: <strong>${cpt}</strong>` : '',
        `</div>`,
      ].join('');
    },
  },
  legend: {
    bottom: 25,
    left: 'center',
    orient: 'horizontal',
    type: 'scroll',
    textStyle: { color: theme.chartAxisLabel() },
    itemGap: 14,
    itemWidth: 14,
    itemHeight: 14,
  },
  grid: {
    left: '8%',
    right: '8%',
    top: '12%',
    bottom: '20%',
    containLabel: true,
  },
  xAxis: {
    type: 'log',
    name: 'Insurance Pay',
    nameTextStyle: { color: theme.chartAxisLabel() },
    axisLabel: { formatter: (val) => `$${Math.round(val).toLocaleString()}` },
    ...buildAxisStyle(),
  },
  yAxis: {
    type: 'log',
    name: 'Patient Payment',
    nameTextStyle: { color: theme.chartAxisLabel() },
    axisLabel: { formatter: (val) => `$${Math.round(val).toLocaleString()}` },
    ...buildAxisStyle(),
  },
  toolbox: buildToolbox(),
  // dataZoom tailored for bubble chart: enable wheel and drag zoom on both axes
  dataZoom: [
    {
      type: 'inside',
      xAxisIndex: [0],
      yAxisIndex: [0],
      filterMode: 'weakFilter',
      zoomOnMouseWheel: true,
      moveOnMouseMove: true,
    },
    {
      type: 'slider',
      xAxisIndex: [0],
      height: 18,
      bottom: 0,
      start: 0,
      end: 100,
      borderColor: theme.chartZoomTrackBorder(),
      backgroundColor: theme.chartZoomTrackBg(),
    },
  ],
  color: getAnalyticsColors(),
  series: (function buildSeries() {
    const points = Array.isArray(data) ? data : [];
    const maxVisits = points.reduce((m, p) => Math.max(m, safeNumber(p.visitCount) || safeNumber((p.value && p.value[2]) || 0)), 0) || 1;

    const sizeFor = (v) => {
      const min = 8;
      const max = 48;
      return Math.round(((v / maxVisits) * (max - min)) + min);
    };

    // Choose category: prefer facility if distinct categories are reasonable, else provider
    const categories = [...new Set(points.map((p) => p.facilityName || p.provider || 'Unknown'))];
    const useFacility = categories.length <= 30;

    const groups = points.reduce((acc, p) => {
      const key = useFacility ? (p.facilityName || 'Unknown') : (p.provider || 'Unknown');
      acc[key] = acc[key] || [];
      const ins = (p.value && p.value[0]) || 0;
      const pat = (p.value && p.value[1]) || 0;
      const visits = p.visitCount || (p.value && p.value[2]) || 1;
      acc[key].push([ins, pat, visits, p.provider, p.facilityName, p.cpt, p.rawInsurancePay, p.rawPatientPayment]);
      return acc;
    }, {});

    return Object.entries(groups).map(([name, arr]) => ({
      name,
      type: 'scatter',
      data: arr,
      symbolSize: function (data) {
        const v = (data && data[2]) || 1;
        return sizeFor(v);
      },
      itemStyle: { opacity: 0.72 },
      emphasis: { itemStyle: { shadowBlur: 18, shadowColor: `rgba(${theme.chartTooltipTextRgb()}, 0.25)` } },
      large: true,
      largeThreshold: 2000,
      animationDuration: 900,
      animationEasing: 'cubicOut',
    }));
  })(),
});

export const completionGaugeOption = ({ value }) => ({
  title: buildTitle('Completion Gauge', 'Percentage of seen appointments'),
  tooltip: { formatter: '{a} <br/>{c}%' },
  series: [
    {
      name: 'Completion',
      type: 'gauge',
      min: 0,
      max: 100,
      progress: { show: true, width: 18 },
      axisLine: { lineStyle: { width: 18 } },
      axisTick: { splitNumber: 5 },
      detail: { valueAnimation: true, formatter: '{value}%', color: theme.chartTitleText(), fontSize: 20 },
      data: [{ value, name: 'Seen %' }],
    },
  ],
  toolbox: buildToolbox(),
});

export const appointmentFunnelOption = ({ data }) => ({
  title: buildTitle('Appointment Funnel', 'Stages from appointments to completion'),
  tooltip: { trigger: 'item', formatter: '{b}: {c}' },
  series: [
    {
      type: 'funnel',
      left: '10%',
      top: 60,
      bottom: 20,
      width: '80%',
      min: 0,
      max: Math.max(...data.map((item) => item.value), 1),
      label: { show: true, position: 'inside' },
      labelLine: { length: 10, lineStyle: { width: 1, type: 'solid' } },
      itemStyle: { borderColor: theme.chartSurface(), borderWidth: 1 },
      emphasis: { label: { fontSize: 16 } },
      data,
    },
  ],
  toolbox: buildToolbox(),
});

export const sunburstOption = ({ data }) => ({
  title: buildTitle('Organization → Facility → Status', 'Multi-level status breakdown'),
  tooltip: { formatter: '{b}: {c}' },
  series: [
    (function buildSunburst() {
      // compute total value of leaves to hide tiny slices' labels
      const sumValues = (nodes) => {
        if (!nodes) return 0;
        return nodes.reduce((acc, node) => {
          if (Array.isArray(node.children) && node.children.length) {
            return acc + sumValues(node.children);
          }
          return acc + (node.value || 0);
        }, 0);
      };

      const total = sumValues(Array.isArray(data) ? data : [data]) || 1;

      return {
        type: 'sunburst',
        data,
        center: ['48%', '55%'],
        // move inner radius outward to free center space
        radius: ['34%', '78%'],
        sort: 'desc',
        nodeClick: 'zoomToNode',
        highlightPolicy: 'descendant',
        // let echarts attempt to avoid overlaps and move labels when possible
        labelLayout: { hideOverlap: true, moveOverlap: true },
        label: {
          rotate: 'tangential',
          color: theme.chartTitleText(),
          overflow: 'truncate',
        },
        levels: [
          {},
          {
            // top-level (organizations) — hide labels
            r0: '0%',
            r: '34%',
            itemStyle: { borderWidth: 0 },
            label: { show: false },
          },
          {
            // mid-level (facilities) — hide small facility labels to reduce clutter
            r0: '34%',
            r: '64%',
            itemStyle: { borderWidth: 1, gapWidth: 6 },
            label: {
              rotate: 'tangential',
              fontSize: 11,
              align: 'center',
              formatter: function (params) {
                const val = params && params.value ? params.value : 0;
                // show facility label only if it represents >= 2% of total
                return val / total >= 0.02 ? params.name : '';
              },
            },
          },
          {
            // outer-level (status) — show status labels when not tiny
            r0: '64%',
            r: '78%',
            itemStyle: { borderWidth: 1, gapWidth: 8 },
            label: {
              rotate: 'tangential',
              fontSize: 12,
              align: 'right',
              formatter: function (params) {
                const val = params && params.value ? params.value : 0;
                // show status label if >= 1% of total
                return val / total >= 0.01 ? params.name : '';
              },
            },
          },
        ],
        emphasis: { label: { show: true, fontSize: 13, fontWeight: 700 } },
      };
    })(),
  ],
  toolbox: buildToolbox(),
});

export const sankeyOption = ({ nodes, links }) => ({
  title: buildTitle('Organization → Facility → Provider', 'Relationship flow across the enterprise'),
  tooltip: { trigger: 'item' },
  series: [
    {
      type: 'sankey',
      emphasis: { focus: 'adjacency' },
      data: nodes,
      links,
      itemStyle: { borderWidth: 1, borderColor: theme.chartAxisLine() },
      lineStyle: { color: 'source', curveness: 0.5 },
      label: { color: theme.chartTooltipText() },
    },
  ],
  toolbox: buildToolbox(),
});

export const geographicalDistributionOption = ({ data }) => ({
  title: buildTitle('US Geographic Distribution', 'State-level appointment distribution'),
  tooltip: { trigger: 'item', formatter: '{b}: {c}' },
  visualMap: {
    min: 0,
    max: Math.max(...data.map((item) => item.value), 1),
    text: ['High', 'Low'],
    realtime: false,
    calculable: true,
    inRange: { color: [theme.chartBlue100(), theme.chartBlue700()] },
    textStyle: { color: theme.chartAxisLabel() },
  },
  series: [
    {
      type: 'map',
      map: 'USA',
      roam: true,
      emphasis: { label: { show: true } },
      data,
    },
  ],
  toolbox: buildToolbox(),
});

export const financialByFacilityOption = ({ data }) => ({
  title: buildTitle('Financial Summary by Facility', 'Insurance pay vs patient payment, totals'),
  tooltip: {
    trigger: 'axis',
    axisPointer: { type: 'shadow' },
    formatter: (params) => {
      const index = params[0]?.dataIndex ?? 0;
      const row = data[index] || {};
      return [
        `<div style="padding: 8px; color: ${theme.chartTooltipText()}; font-size: 13px; line-height: 1.5;">`,
        `<strong>${row.name || ''}</strong>`,
        `<br/>Insurance Pay: <strong>${formatRevenue(row.insurance)}</strong>`,
        `<br/>Patient Payment: <strong>${formatRevenue(row.patient)}</strong>`,
        `<br/>Total: <strong>${formatRevenue(row.total)}</strong>`,
        `<br/>Visits: <strong>${row.visits ?? 'N/A'}</strong>`,
        `</div>`,
      ].join('');
    },
  },
  legend: buildLegend('bottom'),
  grid: buildGrid(),
  xAxis: {
    type: 'category',
    data: data.map((item) => item.name),
    axisTick: { alignWithLabel: true },
    axisLabel: {
      color: theme.chartAxisLabel(),
      fontSize: 11,
      interval: 0,
      rotate: 35,
      formatter: (value) => truncateLabel(value, 16),
    },
    axisLine: { lineStyle: { color: theme.chartAxisLine() } },
  },
  yAxis: {
    type: 'value',
    axisLabel: { formatter: (value) => formatRevenue(value) },
    ...buildAxisStyle(),
  },
  series: [
    {
      name: 'Insurance Pay',
      type: 'bar',
      stack: 'total',
      data: data.map((item) => item.insurance),
      itemStyle: { color: theme.chartHighlight(), borderRadius: [0, 0, 0, 0] },
    },
    {
      name: 'Patient Payment',
      type: 'bar',
      stack: 'total',
      data: data.map((item) => item.patient),
      itemStyle: { color: theme.chartSuccess(), borderRadius: [8, 8, 0, 0] },
    },
  ],
  toolbox: buildToolbox(),
  dataZoom: buildDataZoom(),
});

// ---------- Patient Analytics: Visit Frequency (Bar) ----------
export const visitFrequencyBarOption = ({ data }) => buildBarOption({
  title: 'Visit Frequency',
  subtitle: 'Patients grouped by how often they visit',
  xAxisData: data.map((item) => item.name),
  series: [
    {
      name: 'Patients',
      type: 'bar',
      data: data.map((item) => item.value),
      barMaxWidth: '40%',
      itemStyle: { borderRadius: [6, 6, 0, 0] },
    },
  ],
});

// ---------- Patient Analytics: No-Show Risk (Donut) ----------
export const noShowRiskDonutOption = ({ data }) => ({
  title: buildTitle('No-Show Risk', 'Patients grouped by no-show history'),
  legend: buildLegend('bottom'),
  tooltip: { trigger: 'item', formatter: '{b}: {c} ({d}%)' },
  series: [
    {
      type: 'pie',
      radius: ['42%', '68%'],
      center: ['50%', '48%'],
      label: { show: false },
      emphasis: { label: { show: true, fontSize: 14, fontWeight: 'bold' } },
      data,
    },
  ],
  color: getAnalyticsColors(),
  toolbox: buildToolbox(),
});

// ---------- Patient Analytics: Recency (Funnel) ----------
export const recencyHorizontalBarOption = ({ data = [] }) => ({
  title: buildTitle(
    'Patient Recency',
    'Patients grouped by days since last visit'
  ),

  tooltip: {
    trigger: 'axis',
    axisPointer: { type: 'shadow' },
  },

  grid: buildGrid(),

  xAxis: {
    type: 'value',
    ...buildAxisStyle(),
  },

  yAxis: {
    type: 'category',
    inverse: true,
    data: Array.isArray(data) ? data.map((i) => i.name) : [],
    ...buildAxisStyle(),
  },

  series: [
    {
      type: 'bar',
      data: Array.isArray(data) ? data.map((i) => i.value) : [],
      barWidth: 22,
      itemStyle: {
        borderRadius: [0, 8, 8, 0],
      },
      label: {
        show: true,
        position: 'right',
      },
    },
  ],

  color: getAnalyticsColors(),
  toolbox: buildToolbox(),
});

// ---------- Patient Analytics: Lifetime Value, patient count (Bar) ----------
export const ltvCountBarOption = ({ data }) => buildBarOption({
  title: 'Patients by Lifetime Value',
  subtitle: 'Patient count per revenue tier',
  xAxisData: data.map((item) => item.name),
  series: [
    {
      name: 'Patients',
      type: 'bar',
      data: data.map((item) => item.value),
      barMaxWidth: '40%',
      itemStyle: { borderRadius: [6, 6, 0, 0] },
    },
  ],
});

// ---------- Patient Analytics: Lifetime Value, revenue (Treemap) ----------
export const ltvRevenueTreemapOption = ({ data }) => ({
  title: buildTitle('Revenue Concentration', 'Dollar contribution by lifetime-value tier'),
  tooltip: { formatter: ({ data }) => `${data.name}<br/>Revenue: $${safeNumber(data.value).toLocaleString()}` },
  series: [
    {
      type: 'treemap',
      roam: false,
      nodeClick: false,
      label: { show: true, formatter: '{b}\n$' + '{c}', color: theme.chartTooltipText() },
      data,
    },
  ],
  color: getAnalyticsColors(),
  toolbox: buildToolbox(),
});

// ---------- Patient Analytics: Acquisition Trend (Line) ----------
export const acquisitionTrendLineOption = ({ data }) => ({
  title: buildTitle('Patient Acquisition', 'New patients per month'),
  tooltip: { trigger: 'axis' },
  grid: buildGrid(),
  xAxis: { type: 'category', data: data.map((item) => item.name), boundaryGap: false, ...buildAxisStyle() },
  yAxis: { type: 'value', ...buildAxisStyle() },
  series: [
    {
      name: 'New Patients',
      type: 'line',
      data: data.map((item) => item.value),
      smooth: true,
      symbol: 'circle',
      symbolSize: 7,
      itemStyle: { color: getAnalyticsColors()[2] },
      lineStyle: { width: 3 },
      areaStyle: { opacity: 0.12 },
    },
  ],
  toolbox: buildToolbox(),
  dataZoom: buildDataZoom(),
});

// ---------- Patient Analytics: Retention Rate (Gauge) ----------
export const retentionGaugeOption = ({ value }) => ({
  title: { ...buildTitle('Retention Rate', 'Share of patients active in the last 90 days'), top: 16 },
  tooltip: { formatter: '{a} <br/>{c}%' },
  series: [
    {
      name: 'Retention',
      type: 'gauge',
      center: ['50%', '60%'],
      min: 0,
      max: 100,
      progress: { show: true, width: 18 },
      axisLine: { lineStyle: { width: 18 } },
      axisTick: { splitNumber: 5 },
      detail: { valueAnimation: true, formatter: '{value}%', color: theme.chartTitleText(), fontSize: 20 },
      data: [{ value, name: 'Retained' }],
    },
  ],
  toolbox: buildToolbox(),
});

// ---------- Patient Analytics: Engagement Score (Radar) ----------
export const engagementRadarOption = ({ data }) => ({
  title: buildTitle('Patient Engagement Score', 'Composite view across 5 normalized metrics'),
  tooltip: { trigger: 'item' },
  radar: {
    indicator: [
      { name: 'Show Rate', max: 100 },
      { name: 'Retention', max: 100 },
      { name: 'Visit Frequency', max: 100 },
      { name: 'Revenue', max: 100 },
      { name: 'Low No-Show', max: 100 },
    ],
    shape: 'circle',
    splitLine: { lineStyle: { color: theme.chartAxisLine() } },
    axisLine: { lineStyle: { color: theme.chartAxisLine() } },
    splitArea: { areaStyle: { color: 'transparent' } },
    name: { color: theme.chartAxisLabel() },
  },
  series: [
    {
      name: 'Engagement',
      type: 'radar',
      data: [{ name: 'This Facility', value: data }],
      areaStyle: { opacity: 0.25, color: getAnalyticsColors()[0] },
      itemStyle: { color: getAnalyticsColors()[0] },
      lineStyle: { color: getAnalyticsColors()[0] },
    },
  ],
  toolbox: buildToolbox(),
});

// ---------- Patient Analytics: Daily Active Patients (Calendar Heatmap) ----------
export const dailyActiveCalendarOption = ({ data = [], startDate, endDate }) => {
  const safeData = Array.isArray(data) ? data : [];

  // normalize items into [dateString, number] pairs and filter invalid entries
  const normalized = safeData
    .reduce((acc, item) => {
      if (Array.isArray(item) && item.length >= 2) {
        acc.push([String(item[0] ?? ''), safeNumber(item[1])]);
      } else if (item && typeof item === 'object' && (item.date || item.active_patients || item.value)) {
        const date = String(item.date ?? item[0] ?? '');
        const val = safeNumber(item.active_patients ?? item.value ?? item[1]);
        acc.push([date, val]);
      }
      return acc;
    }, [])
    .filter((pair) => pair[0] && Number.isFinite(pair[1]));

  // sort by date
  normalized.sort((a, b) => new Date(a[0]) - new Date(b[0]));

  const maxVal = Math.max(...(normalized.map((item) => item[1]) || []), 1);

  // determine calendar range: prefer explicit start/end, else derive from data if available
  let range;
  const s = startDate ?? (normalized.length ? normalized[0][0] : null);
  const e = endDate ?? (normalized.length ? normalized[normalized.length - 1][0] : null);
  if (s && e) {
    const sStr = String(s);
    const eStr = String(e);
    if (sStr && eStr && sStr !== 'undefined' && eStr !== 'undefined') range = [sStr, eStr];
  }

  const calendar = {
    top: 120,
    left: 40,
    right: 20,
    cellSize: ['auto', 16],
    itemStyle: { borderWidth: 2, borderColor: theme.chartSurface() },
    yearLabel: { show: false },
    dayLabel: { color: theme.chartDayLabel(), fontSize: 10 },
    monthLabel: { color: theme.chartAxisLabel(), fontSize: 11 },
  };
  if (range) calendar.range = range;

  return {
    title: buildTitle('Daily Active Patients', 'Patient activity across the selected date range'),
    tooltip: { formatter: (params) => `${params.value[0]}<br/>Active Patients: <strong>${params.value[1]}</strong>` },
    visualMap: {
      min: 0,
      max: maxVal,
      calculable: true,
      orient: 'horizontal',
      left: 'center',
      bottom: 0,
      inRange: { color: [theme.chartAreaLight(), getAnalyticsColors()[0]] },
      textStyle: { color: theme.chartAxisLabel() },
    },
    calendar,
    series: [{ type: 'heatmap', coordinateSystem: 'calendar', data: normalized }],
    toolbox: buildToolbox(),
  };
};

// ---------- Patient Analytics: Frequency-to-Recency Flow (Sankey) ----------
export const frequencyRecencySankeyOption = ({ nodes, links }) => ({
  title: buildTitle('Frequency → Recency Flow', 'Where each visit-frequency segment ends up'),
  tooltip: { trigger: 'item' },
  series: [
    {
      type: 'sankey',
      emphasis: { focus: 'adjacency' },
      data: nodes,
      links,
      itemStyle: { borderWidth: 1, borderColor: theme.chartAxisLine() },
      lineStyle: { color: 'gradient', curveness: 0.5 },
      label: { color: theme.chartTooltipText() },
    },
  ],
  color: getAnalyticsColors(),
  toolbox: buildToolbox(),
});

// ---------- Patient Analytics: Visit Count Spread (Boxplot, optional) ----------
export const cptPolarEndAngleOption = ({ data }) => ({
  title: buildTitle(
    'CPT Code Distribution',
    'Unique patients by CPT code'
  ),

  tooltip: {
    trigger: 'item',
    formatter: (p) =>
      `<b>${p.name}</b><br/>Patients: ${p.value}`,
  },

  angleAxis: {
    type: 'category',
    startAngle: 90,
    endAngle: -270,
    data: data.map((i) => i.name),
    axisLabel: {
      rotate: 25,
      fontSize: 11,
      margin: 20,
    },
  },

  radiusAxis: {
    type: 'value',
  },

  polar: {
    radius: '60%',
  },

  series: [
    {
      type: 'bar',

      coordinateSystem: 'polar',

      roundCap: true,

      barWidth: 12,

      data: data.map((i) => ({
        value: i.value,
        name: i.name,
      })),

      label: {
        show: false,
        position: 'outside',
        formatter: '{c}',
      },

      itemStyle: {
        borderRadius: 8,
      },
    },
  ],

  color: getAnalyticsColors(),

  toolbox: buildToolbox(),
});

// ---------- Provider Analytics ----------

const BRAND_GRADIENTS = {
  primary: {
    type: 'linear', x: 0, y: 0, x2: 1, y2: 0,
    colorStops: [{ offset: 0, color: '#52c4c8' }, { offset: 1, color: '#26959a' }]
  },
  primaryVertical: {
    type: 'linear', x: 0, y: 0, x2: 0, y2: 1,
    colorStops: [{ offset: 0, color: '#52c4c8' }, { offset: 1, color: '#26959a' }]
  },
  accentVertical: {
    type: 'linear', x: 0, y: 0, x2: 0, y2: 1,
    colorStops: [{ offset: 0, color: '#f5b942' }, { offset: 1, color: '#e6a832' }]
  }
};

const buildColorRamp = (count) => {
  const palette = [
    '#26959a', // Teal
    '#f59e0b', // Warm Amber / Gold
    '#0284c7', // Sky Blue
    '#ea580c', // Tangerine Orange
    '#8b5cf6', // Violet
    '#10b981', // Emerald
    '#ec4899', // Pink
    '#06b6d4', // Bright Cyan
    '#6366f1', // Indigo
    '#84cc16', // Lime
  ];
  if (count <= 1) return [palette[0]];
  return Array.from({ length: count }, (_, i) => palette[i % palette.length]);
};

export const providerProductivityGaugeOption = ({ value }) => ({
  title: buildTitle('Productivity Score', 'Composite score from visit volume, revenue, and no-show rate'),
  tooltip: { formatter: '{a} <br/>{c}%' },
  series: [
    {
      name: 'Productivity',
      type: 'gauge',
      min: 0,
      max: 100,
      progress: {
        show: true,
        width: 18,
        itemStyle: {
          color: BRAND_GRADIENTS.primary,
        },
      },
      axisLine: {
        lineStyle: {
          width: 18,
          color: [[1, theme.feeTealLight()]],
        },
      },
      axisTick: { splitNumber: 5, lineStyle: { color: theme.chartAxisLine() } },
      splitLine: { lineStyle: { color: '#4dc3c7', width: 2 } },
      pointer: { itemStyle: { color: '#26959a' } },
      detail: {
        valueAnimation: true,
        formatter: '{value}%',
        color: theme.feeTealDark(),
        fontSize: 22,
        fontWeight: 'bold',
      },
      data: [{ value, name: 'Score' }],
    },
  ],
  toolbox: buildToolbox(),
});

export const providerScoreRadarOption = ({ data }) => ({
  title: buildTitle('Productivity Breakdown', 'Normalized components behind the productivity score'),
  tooltip: { trigger: 'item' },
  radar: {
    indicator: [
      { name: 'Seen Rate', max: 100 },
      { name: 'Visit Volume', max: 100 },
      { name: 'Revenue', max: 100 },
      { name: 'Low No-Show', max: 100 },
    ],
    shape: 'circle',
    splitLine: { lineStyle: { color: theme.chartAxisLine() } },
    axisLine: { lineStyle: { color: theme.chartAxisLine() } },
    splitArea: {
      areaStyle: {
        color: theme.chartRadarAreas(),
      },
    },
    name: { color: theme.chartTitleText(), fontWeight: 600, fontSize: 12 },
  },
  series: [
    {
      name: 'Productivity',
      type: 'radar',
      data: [{ name: 'This Provider', value: data }],
      areaStyle: {
        opacity: 0.25,
        color: {
          type: 'linear',
          x: 0,
          y: 0,
          x2: 1,
          y2: 1,
          colorStops: [
            { offset: 0, color: 'rgba(60, 159, 162, 0.45)' },
            { offset: 1, color: 'rgba(38, 149, 154, 0.15)' },
          ],
        },
      },
      itemStyle: { color: '#26959a' },
      lineStyle: { color: '#3c9fa2', width: 2.5 },
    },
  ],
  toolbox: buildToolbox(),
});

export const providerTrendOption = ({ buckets, patients, visits, revenue }) => ({
  title: buildTitle('Provider Trend', 'Patients, visits, and revenue over time'),
  tooltip: { trigger: 'axis' },
  legend: buildLegend('bottom'),
  grid: buildGrid(),
  xAxis: { type: 'category', data: buckets, boundaryGap: false, ...buildAxisStyle() },
  yAxis: [
    { type: 'value', name: 'Count', ...buildAxisStyle() },
    { type: 'value', name: 'Revenue', axisLabel: { formatter: (value) => formatRevenue(value) }, ...buildAxisStyle() },
  ],
  series: [
    {
      name: 'Patients',
      type: 'line',
      data: patients,
      smooth: true,
      symbol: 'circle',
      symbolSize: 6,
      itemStyle: { color: '#26959a' },
      lineStyle: { width: 3, color: '#26959a' },
      areaStyle: {
        color: {
          type: 'linear',
          x: 0,
          y: 0,
          x2: 0,
          y2: 1,
          colorStops: [
            { offset: 0, color: 'rgba(38, 149, 154, 0.18)' },
            { offset: 1, color: 'rgba(38, 149, 154, 0.01)' },
          ],
        },
      },
    },
    {
      name: 'Visits',
      type: 'line',
      data: visits,
      smooth: true,
      symbol: 'circle',
      symbolSize: 6,
      itemStyle: { color: '#4dc3c7' },
      lineStyle: { width: 3, color: '#4dc3c7' },
      areaStyle: {
        color: {
          type: 'linear',
          x: 0,
          y: 0,
          x2: 0,
          y2: 1,
          colorStops: [
            { offset: 0, color: 'rgba(77, 195, 199, 0.18)' },
            { offset: 1, color: 'rgba(77, 195, 199, 0.01)' },
          ],
        },
      },
    },
    {
      name: 'Revenue',
      type: 'line',
      yAxisIndex: 1,
      data: revenue,
      smooth: true,
      symbol: 'circle',
      symbolSize: 6,
      itemStyle: { color: '#f5b942' },
      lineStyle: { width: 3, color: '#f5b942', type: 'dashed' },
    },
  ],
  color: ['#26959a', '#4dc3c7', '#f5b942'],
  toolbox: buildToolbox(),
  dataZoom: buildDataZoom(),
});

export const providerHorizontalBarOption = ({ data, title, subtitle, color, multiColor = false }) => {
  const ramp = multiColor ? buildColorRamp(data.length) : null;
  const showZoom = data.length > 10;
  const defaultBarGradient = {
    type: 'linear',
    x: 0,
    y: 0,
    x2: 1,
    y2: 0,
    colorStops: [
      { offset: 0, color: '#52c4c8' },
      { offset: 1, color: '#26959a' },
    ],
  };

  return {
    title: buildTitle(title, subtitle),
    tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
    grid: buildGrid(),
    xAxis: { type: 'value', ...buildAxisStyle() },
    yAxis: { type: 'category', inverse: true, data: data.map((item) => item.name), ...buildAxisStyle() },
    series: [
      {
        type: 'bar',
        data: ramp
          ? data.map((item, idx) => ({ value: item.value, itemStyle: { color: ramp[idx], borderRadius: [0, 6, 6, 0] } }))
          : data.map((item) => item.value),
        barWidth: 18,
        itemStyle: ramp ? undefined : { color: color || defaultBarGradient, borderRadius: [0, 6, 6, 0] },
      },
    ],
    dataZoom: showZoom ? [
      { type: 'inside', yAxisIndex: 0, start: 0, end: Math.round((10 / data.length) * 100) },
      { type: 'slider', yAxisIndex: 0, right: 6, width: 10, start: 0, end: Math.round((10 / data.length) * 100) },
    ] : undefined,
    toolbox: buildToolbox(),
  };
};

export const providerDotPlotOption = ({ data, title, subtitle }) => {
  const maxVal = Math.max(...data.map((item) => item.value), 1);
  const ramp = buildColorRamp(data.length);
  const showZoom = data.length > 10;
  return {
    title: buildTitle(title, subtitle),
    tooltip: { trigger: 'item', formatter: (p) => `${p.name}: ${p.value[0]}` },
    grid: buildGrid(),
    xAxis: { type: 'value', ...buildAxisStyle() },
    yAxis: { type: 'category', inverse: true, data: data.map((item) => item.name), ...buildAxisStyle() },
    series: [
      {
        type: 'scatter',
        symbolSize: (value) => 10 + (value[0] / maxVal) * 20,
        data: data.map((item, idx) => ({ name: item.name, value: [item.value, idx], itemStyle: { color: ramp[idx] } })),
      },
    ],
    dataZoom: showZoom ? [
      { type: 'inside', yAxisIndex: 0, start: 0, end: Math.round((10 / data.length) * 100) },
      { type: 'slider', yAxisIndex: 0, right: 6, width: 10, start: 0, end: Math.round((10 / data.length) * 100) },
    ] : undefined,
    toolbox: buildToolbox(),
  };
};

export const providerHeatmapStripOption = ({ data, title, subtitle }) => {
  const maxVal = Math.max(...data.map((item) => item.value), 1);
  const showZoom = data.length > 12;
  return {
    title: buildTitle(title, subtitle),
    tooltip: { trigger: 'item', formatter: (p) => `${p.name}: ${p.data[2]}` },
    grid: { left: '30%', right: '8%', top: 70, bottom: 50 },
    xAxis: { type: 'category', data: ['Patients'], show: false },
    yAxis: { type: 'category', inverse: true, data: data.map((item) => item.name), ...buildAxisStyle() },
    visualMap: {
      min: 0,
      max: maxVal,
      calculable: true,
      orient: 'horizontal',
      left: 'center',
      bottom: 0,
      inRange: { color: [theme.feeTealLight(), theme.feeTealMid(), '#2ba5aa'] },
      textStyle: { color: theme.chartAxisLabel(), fontWeight: 600 },
    },
    series: [
      {
        type: 'heatmap',
        data: data.map((item, idx) => [0, idx, item.value]),
        label: {
          show: true,
          formatter: (p) => p.data[2],
          color: theme.chartTitleText(),
          fontWeight: 700,
          fontSize: 13,
        },
        itemStyle: { borderColor: theme.chartSurface(), borderWidth: 2 },
      },
    ],
    dataZoom: showZoom ? [
      { type: 'inside', yAxisIndex: 0, start: 0, end: Math.round((12 / data.length) * 100) },
      { type: 'slider', yAxisIndex: 0, right: 6, width: 10, start: 0, end: Math.round((12 / data.length) * 100) },
    ] : undefined,
    toolbox: buildToolbox(),
  };
};

export const providerCityAreaOption = ({ data, title, subtitle }) => ({
  title: buildTitle(title, subtitle),
  tooltip: { trigger: 'axis' },
  grid: buildGrid(),
  xAxis: {
    type: 'category',
    data: data.map((item) => item.name),
    boundaryGap: false,
    axisLabel: { color: theme.chartAxisLabel(), fontSize: 11, interval: 0, rotate: 30 },
    axisLine: { lineStyle: { color: theme.chartAxisLine() } },
  },
  yAxis: { type: 'value', ...buildAxisStyle() },
  series: [
    {
      name: 'Patients',
      type: 'line',
      data: data.map((item) => item.value),
      smooth: true,
      symbol: 'circle',
      symbolSize: 7,
      itemStyle: { color: '#26959a' },
      lineStyle: { width: 3, color: '#26959a' },
      areaStyle: {
        color: {
          type: 'linear', x: 0, y: 0, x2: 0, y2: 1,
          colorStops: [
            { offset: 0, color: 'rgba(60, 159, 162, 0.28)' },
            { offset: 1, color: 'rgba(60, 159, 162, 0.01)' },
          ],
        },
      },
    },
  ],
  toolbox: buildToolbox(),
  dataZoom: data.length > 10 ? buildDataZoom() : undefined,
});

export const providerRoseOption = ({ data, title, subtitle }) => ({
  title: buildTitle(title, subtitle),
  tooltip: { trigger: 'item', formatter: '{b}: {c} ({d}%)' },
  legend: buildLegend('bottom'),
  series: [
    {
      type: 'pie',
      radius: ['12%', '70%'],
      center: ['50%', '52%'],
      roseType: 'radius',
      itemStyle: { borderRadius: 6, borderColor: theme.chartSurface(), borderWidth: 2 },
      label: { show: false },
      emphasis: { label: { show: true, fontSize: 13, fontWeight: 'bold' } },
      data,
    },
  ],
  color: buildColorRamp(data.length || 6),
  toolbox: buildToolbox(),
});

export const providerBenchmarkBarOption = ({ data }) => ({
  title: buildTitle('Provider vs. Organization', 'Provider value against the organization average, by metric'),
  tooltip: {
    trigger: 'axis',
    axisPointer: { type: 'shadow' },
    formatter: (params) => {
      const index = params[0]?.dataIndex ?? 0;
      const row = data[index] || {};
      const pct = row.percentile != null ? `${row.percentile}th percentile` : 'N/A';
      return [
        `<div style="padding: 8px; color: ${theme.chartTooltipText()}; font-size: 13px; line-height: 1.5;">`,
        `<strong>${row.name || ''}</strong>`,
        `<br/>This provider: <strong>${row.providerValue ?? 0}</strong>`,
        `<br/>Org average: <strong>${row.orgMean ?? 0}</strong>`,
        `<br/>Percentile: <strong>${pct}</strong>`,
        `</div>`,
      ].join('');
    },
  },
  legend: buildLegend('bottom'),
  grid: buildGrid(),
  xAxis: {
    type: 'category',
    data: data.map((item) => item.name),
    axisTick: { alignWithLabel: true },
    axisLabel: { color: theme.chartAxisLabel(), fontSize: 11, interval: 0, rotate: 20 },
    axisLine: { lineStyle: { color: theme.chartAxisLine() } },
  },
  yAxis: { type: 'value', ...buildAxisStyle() },
  series: [
    {
      name: 'This provider',
      type: 'bar',
      data: data.map((item) => item.providerValue),
      itemStyle: {
        color: BRAND_GRADIENTS.primaryVertical,
        borderRadius: [6, 6, 0, 0],
      },
    },
    {
      name: 'Org average',
      type: 'bar',
      data: data.map((item) => item.orgMean),
      itemStyle: {
        color: BRAND_GRADIENTS.accentVertical,
        borderRadius: [6, 6, 0, 0],
      },
    },
  ],
  toolbox: buildToolbox(),
});

// ---------- Fee Schedule Analytics ----------

const formatUsd = (value) => `$${safeNumber(value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const buildZoomWindow = (count, visible, axis = 'x') => {
  if (count <= visible) return undefined;
  const end = Math.round((visible / count) * 100);
  if (axis === 'x') return buildDataZoom().map((zoom) => ({ ...zoom, end }));
  return [
    { type: 'inside', yAxisIndex: 0, start: 0, end },
    { type: 'slider', yAxisIndex: 0, right: 6, width: 10, start: 0, end },
  ];
};

/** Horizontal bars with value labels; rows are { name, value }. */
export const feeBarOption = ({ data, title, subtitle, currency = false, color = theme.feeTeal() }) => {
  const axisStyle = buildAxisStyle();
  const format = (value) => (currency ? formatUsd(value) : safeNumber(value).toLocaleString());
  return {
    title: buildTitle(title, subtitle),
    tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' }, formatter: (params) => `${params[0].name}: <strong>${format(params[0].value)}</strong>` },
    grid: { ...buildGrid(), left: '3%', right: '14%' },
    xAxis: { type: 'value', ...axisStyle, axisLabel: { ...axisStyle.axisLabel, formatter: (value) => (currency ? formatRevenue(value) : value.toLocaleString()) } },
    yAxis: { type: 'category', inverse: true, data: data.map((item) => item.name), ...axisStyle, axisLabel: { ...axisStyle.axisLabel, formatter: (value) => truncateLabel(value, window.innerWidth < 576 ? 14 : 26) } },
    series: [
      {
        type: 'bar',
        barMaxWidth: 18,
        // An item may carry its own color to single it out from the series color.
        data: data.map((item) => (item.color ? { value: item.value, itemStyle: { color: item.color } } : item.value)),
        itemStyle: { color, borderRadius: [0, 4, 4, 0] },
        label: { show: true, position: 'right', formatter: (params) => format(params.value), color: theme.chartAxisLabel(), fontSize: 11 },
      },
    ],
    dataZoom: buildZoomWindow(data.length, 15, 'y'),
    toolbox: buildToolbox(),
  };
};

/** Provider-count histogram of rates with the zip median and an optional highlighted provider marked. */
export const feeHistogramOption = ({ bins, median, highlight }) => {
  const axisStyle = buildAxisStyle();
  const binOf = (value) => bins.findIndex((bin, idx) => value >= bin.from && (value < bin.to || idx === bins.length - 1));
  const marks = [{ name: 'Zip median', xAxis: binOf(median), lineStyle: { color: theme.feeTealDark(), type: 'dashed' }, label: { formatter: `Median ${formatUsd(median)}` } }];
  if (highlight) {
    marks.push({ name: 'Selected provider', xAxis: binOf(highlight.median), lineStyle: { color: theme.gradientEnd(), width: 2 }, label: { formatter: `${highlight.name} ${formatUsd(highlight.median)}` } });
  }
  return {
    title: buildTitle('Rate Distribution in Zip', 'Providers per rate band'),
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      formatter: (params) => {
        const bin = bins[params[0].dataIndex] || {};
        return `${formatUsd(bin.from)} – ${formatUsd(bin.to)}<br/><strong>${safeNumber(bin.count).toLocaleString()}</strong> providers`;
      },
    },
    grid: buildGrid(),
    xAxis: { type: 'category', data: bins.map((bin) => formatRevenue(bin.from)), ...axisStyle },
    yAxis: { type: 'value', name: 'Providers', minInterval: 1, ...axisStyle },
    series: [
      {
        type: 'bar',
        barCategoryGap: '8%',
        data: bins.map((bin) => bin.count),
        itemStyle: { color: theme.feeTealMid(), borderColor: theme.feeTeal(), borderWidth: 1, borderRadius: [4, 4, 0, 0] },
        markLine: { symbol: 'none', label: { position: 'insideEndTop', color: theme.chartAxisLabel(), fontSize: 11 }, data: marks },
      },
    ],
    toolbox: buildToolbox(),
  };
};

/** Lowest-to-highest price per code with the median dot; rows are { name, min, median, max }. */
export const feeRangeOption = ({ data, title, subtitle }) => {
  const axisStyle = buildAxisStyle();
  return {
    title: buildTitle(title, subtitle),
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      formatter: (params) => {
        const row = data[params[0].dataIndex] || {};
        const summary = `<strong>${row.name}</strong><br/>Highest: <strong>${formatUsd(row.max)}</strong><br/>Median: <strong>${formatUsd(row.median)}</strong><br/>Lowest: <strong>${formatUsd(row.min)}</strong>`;
        // Optional per-network prices: [{ network, min, max }].
        const networks = (row.networks || []).map((n) => `${n.network}: <strong>${n.min === n.max ? formatUsd(n.min) : `${formatUsd(n.min)} – ${formatUsd(n.max)}`}</strong>`);
        return networks.length ? `${summary}<br/><br/><strong>By network</strong><br/>${networks.join('<br/>')}` : summary;
      },
    },
    grid: { ...buildGrid(), left: '3%' },
    xAxis: { type: 'value', ...axisStyle, axisLabel: { ...axisStyle.axisLabel, formatter: (value) => formatRevenue(value) } },
    yAxis: { type: 'category', inverse: true, data: data.map((item) => item.name), ...axisStyle },
    series: [
      { name: 'Base', type: 'bar', stack: 'range', silent: true, itemStyle: { color: 'transparent' }, data: data.map((item) => item.min) },
      { name: 'Price range', type: 'bar', stack: 'range', barMaxWidth: 10, itemStyle: { color: theme.feeTealMid(), borderRadius: 4 }, data: data.map((item) => item.max - item.min) },
      { name: 'Median', type: 'scatter', symbolSize: 10, itemStyle: { color: theme.feeTealDark(), borderColor: theme.chartSurface(), borderWidth: 2 }, data: data.map((item) => item.median) },
    ],
    dataZoom: buildZoomWindow(data.length, 15, 'y'),
    toolbox: buildToolbox(),
  };
};

export const feeShareDonutOption = ({ data, title, subtitle }) => ({
  title: buildTitle(title, subtitle),
  legend: buildLegend('bottom'),
  tooltip: { trigger: 'item', formatter: (params) => `${params.name}: <strong>${params.value.toLocaleString()}</strong> (${params.percent}%)` },
  series: [
    {
      type: 'pie',
      radius: ['44%', '68%'],
      center: ['50%', '52%'],
      avoidLabelOverlap: true,
      minShowLabelAngle: 4,
      itemStyle: { borderColor: theme.chartSurface(), borderWidth: 2, borderRadius: 4 },
      label: { show: true, formatter: '{b}\n{d}%', color: theme.chartAxisLabel(), fontSize: 11 },
      data,
    },
  ],
  color: getAnalyticsColors(),
  toolbox: buildToolbox(),
});

// ---------- Revenue Analytics ----------

/** Monthly charges, collections and adjustments with the net collection rate; rows are monthly entries from getOrganizationRevenue. */
export const revenueCollectionsTrendOption = ({
  data,
  title = 'Charges vs. Collections',
  subtitle = 'Monthly billed charges, collections, adjustments and net collection rate',
}) => {
  const axisStyle = buildAxisStyle();
  const colors = getAnalyticsColors();
  const bars = [
    { key: 'charges', name: 'Billed charges', color: colors[1] },
    { key: 'collected', name: 'Collected', color: colors[0] },
    { key: 'adjustment', name: 'Adjustments', color: colors[2] },
  ];
  return {
    title: buildTitle(title, subtitle),
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      formatter: (params) => {
        const row = data[params[0].dataIndex] || {};
        return [
          `<strong>${row.label}</strong>`,
          ...bars.map((bar) => `${bar.name}: <strong>${formatUsd(row[bar.key])}</strong>`),
          `Net collection rate: <strong>${row.net_collection_rate ?? '—'}%</strong>`,
          `Visits: <strong>${safeNumber(row.visits).toLocaleString()}</strong>`,
        ].join('<br/>');
      },
    },
    legend: buildLegend('bottom'),
    grid: { ...buildGrid(), left: '3%', right: '4%' },
    xAxis: { type: 'category', data: data.map((row) => row.label), ...axisStyle },
    yAxis: [
      { type: 'value', ...axisStyle, axisLabel: { ...axisStyle.axisLabel, formatter: (value) => formatRevenue(value) } },
      { type: 'value', min: 0, max: 100, ...axisStyle, splitLine: { show: false }, axisLabel: { ...axisStyle.axisLabel, formatter: '{value}%' } },
    ],
    series: [
      ...bars.map((bar) => ({
        name: bar.name,
        type: 'bar',
        barMaxWidth: 18,
        itemStyle: { color: bar.color, borderRadius: [3, 3, 0, 0] },
        data: data.map((row) => safeNumber(row[bar.key])),
      })),
      {
        name: 'Net collection rate',
        type: 'line',
        yAxisIndex: 1,
        smooth: true,
        symbol: 'circle',
        symbolSize: 6,
        itemStyle: { color: colors[3] },
        lineStyle: { width: 2, color: colors[3] },
        data: data.map((row) => row.net_collection_rate),
      },
    ],
    dataZoom: buildZoomWindow(data.length, 12),
    toolbox: buildToolbox(),
  };
};

/** Waterfall from billed charges down to what is still unpaid; summary is getOrganizationRevenue's summary. */
export const revenueWaterfallOption = ({ summary }) => {
  const axisStyle = buildAxisStyle();
  const colors = getAnalyticsColors();
  const charges = safeNumber(summary?.charges);
  const steps = [
    { name: 'Adjustments', value: safeNumber(summary?.adjustment), color: colors[2] },
    { name: 'Insurance paid', value: safeNumber(summary?.insurance_pay), color: colors[0] },
    { name: 'Patient paid', value: safeNumber(summary?.patient_payment), color: colors[4] },
  ];
  let remaining = charges;
  const floating = steps.map((step) => {
    remaining -= step.value;
    return { ...step, base: Math.max(remaining, 0) };
  });
  const unpaid = Math.max(remaining, 0);
  const bars = [
    { name: 'Billed charges', value: charges, base: 0, color: colors[1] },
    ...floating,
    { name: 'Net unpaid', value: unpaid, base: 0, color: theme.chartHighlightEmphasis() },
  ];
  return {
    title: buildTitle('Where Billed Charges Go', 'Charges less write-offs and payments'),
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      formatter: (params) => {
        const bar = bars[params[0].dataIndex] || {};
        const share = charges ? ` (${((bar.value / charges) * 100).toFixed(1)}% of charges)` : '';
        return `${bar.name}: <strong>${formatUsd(bar.value)}</strong>${share}`;
      },
    },
    grid: { ...buildGrid(), left: '3%', right: '4%' },
    xAxis: { type: 'category', data: bars.map((bar) => bar.name), ...axisStyle, axisLabel: { ...axisStyle.axisLabel, interval: 0, formatter: (value) => truncateLabel(value, 12) } },
    yAxis: { type: 'value', ...axisStyle, axisLabel: { ...axisStyle.axisLabel, formatter: (value) => formatRevenue(value) } },
    series: [
      { type: 'bar', stack: 'waterfall', silent: true, itemStyle: { color: 'transparent' }, data: bars.map((bar) => bar.base) },
      {
        type: 'bar',
        stack: 'waterfall',
        barMaxWidth: 48,
        data: bars.map((bar) => ({ value: bar.value, itemStyle: { color: bar.color, borderRadius: [3, 3, 0, 0] } })),
        label: { show: true, position: 'top', formatter: (params) => formatRevenue(params.value), color: theme.chartAxisLabel(), fontSize: 11 },
      },
    ],
    toolbox: buildToolbox(),
  };
};

/** Outstanding balance per payer split by age since service; payerAging is getOrganizationRevenue's payer_aging. */
export const revenuePayerAgingOption = ({ payerAging }) => {
  const axisStyle = buildAxisStyle();
  const colors = getAnalyticsColors();
  const payers = payerAging?.payers || [];
  return {
    title: buildTitle('Outstanding Balance by Payer & Age', 'Payers carrying the largest unpaid balances, by days since service'),
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      formatter: (params) => {
        const total = params.reduce((sum, p) => sum + safeNumber(p.value), 0);
        const lines = params.filter((p) => safeNumber(p.value) > 0).map((p) => `${p.marker}${p.seriesName} days: <strong>${formatUsd(p.value)}</strong>`);
        return `<strong>${params[0].axisValue}</strong><br/>${lines.join('<br/>')}<br/>Total: <strong>${formatUsd(total)}</strong>`;
      },
    },
    legend: buildLegend('bottom'),
    grid: { ...buildGrid(), left: '3%', right: '6%' },
    xAxis: { type: 'value', ...axisStyle, axisLabel: { ...axisStyle.axisLabel, formatter: (value) => formatRevenue(value) } },
    yAxis: { type: 'category', inverse: true, data: payers, ...axisStyle, axisLabel: { ...axisStyle.axisLabel, formatter: (value) => truncateLabel(value, window.innerWidth < 576 ? 12 : 22) } },
    series: (payerAging?.series || []).map((series, idx) => ({
      name: series.bucket,
      type: 'bar',
      stack: 'aging',
      barMaxWidth: 20,
      itemStyle: { color: colors[(idx + 5) % colors.length] },
      data: series.values,
    })),
    toolbox: buildToolbox(),
  };
};

/** How long payments take: visits per days-to-deposit bucket and the cumulative share of collections; rows are payment_lag entries. */
export const revenuePaymentLagOption = ({ data }) => {
  const axisStyle = buildAxisStyle();
  const colors = getAnalyticsColors();
  const totalCollected = data.reduce((sum, row) => sum + safeNumber(row.collected), 0);
  let running = 0;
  const cumulative = data.map((row) => {
    running += safeNumber(row.collected);
    return totalCollected ? Number(((running / totalCollected) * 100).toFixed(1)) : 0;
  });
  return {
    title: buildTitle('Time to Payment', 'Paid visits by days from service to deposit'),
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      formatter: (params) => {
        const row = data[params[0].dataIndex] || {};
        return [
          `<strong>${row.bucket} days</strong>`,
          `Paid visits: <strong>${safeNumber(row.visits).toLocaleString()}</strong>`,
          `Collected: <strong>${formatUsd(row.collected)}</strong>`,
          `Cumulative share of collections: <strong>${cumulative[params[0].dataIndex]}%</strong>`,
        ].join('<br/>');
      },
    },
    legend: buildLegend('bottom'),
    grid: { ...buildGrid(), left: '3%', right: '4%' },
    xAxis: { type: 'category', data: data.map((row) => `${row.bucket}d`), ...axisStyle },
    yAxis: [
      { type: 'value', minInterval: 1, ...axisStyle },
      { type: 'value', min: 0, max: 100, ...axisStyle, splitLine: { show: false }, axisLabel: { ...axisStyle.axisLabel, formatter: '{value}%' } },
    ],
    series: [
      { name: 'Paid visits', type: 'bar', barMaxWidth: 32, itemStyle: { color: colors[0], borderRadius: [3, 3, 0, 0] }, data: data.map((row) => safeNumber(row.visits)) },
      { name: 'Cumulative % collected', type: 'line', yAxisIndex: 1, smooth: true, symbol: 'circle', symbolSize: 6, itemStyle: { color: colors[3] }, lineStyle: { width: 2, color: colors[3] }, data: cumulative },
    ],
    toolbox: buildToolbox(),
  };
};
